// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

/**
 * Dynamic Mantine Provider
 *
 * Wraps MantineProvider with dynamic theme support based on AppThemeContext.
 * Merges the base theme configuration with the current app theme's color overrides.
 */

import { MantineProvider, createTheme, mergeThemeOverrides } from '@mantine/core';
import type { CSSVariablesResolver } from '@mantine/core';
import type { JSX, ReactNode } from 'react';
import { useAppTheme, bodyTextColors, lightVariantTextColors } from '../contexts/AppThemeContext';

// Base theme configuration (non-color settings)
const baseTheme = createTheme({
  // Clean system font stack
  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
  headings: {
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
    sizes: {
      h1: { fontSize: '1.25rem', fontWeight: '600', lineHeight: '1.4' },
      h2: { fontSize: '1.125rem', fontWeight: '600', lineHeight: '1.4' },
      h3: { fontSize: '1rem', fontWeight: '600', lineHeight: '1.4' },
      h4: { fontSize: '0.875rem', fontWeight: '600', lineHeight: '1.4' },
    },
  },
  fontSizes: {
    xs: '0.6875rem',
    sm: '0.8125rem',
    md: '0.875rem',
    lg: '0.9375rem',
    xl: '1rem',
  },
  radius: {
    xs: '2px',
    sm: '3px',
    md: '4px',
    lg: '6px',
    xl: '8px',
  },
  spacing: {
    xs: '4px',
    sm: '8px',
    md: '12px',
    lg: '16px',
    xl: '24px',
  },
  components: {
    Paper: {
      defaultProps: {
        shadow: undefined,
        withBorder: false,
      },
    },
    Card: {
      defaultProps: {
        shadow: undefined,
        withBorder: false,
      },
    },
    Button: {
      defaultProps: {
        radius: 'sm',
      },
    },
    ActionIcon: {
      defaultProps: {
        radius: 'sm',
      },
    },
    TextInput: {
      defaultProps: {
        radius: 'sm',
      },
    },
    Select: {
      defaultProps: {
        radius: 'sm',
      },
    },
    Tabs: {
      defaultProps: {
        radius: 0,
      },
    },
  },
});

interface DynamicMantineProviderProps {
  readonly children: ReactNode;
}

/*
 * Recomputes the two text colors Mantine derives per palette — the
 * `variant="light"` label and the `c="<name>"` body color — for every palette
 * the active theme registers.
 *
 * Mantine derives that color from shade 6 over a 10% tint of itself, which is
 * about 3.2:1. The literals in index.css cover Mantine's stock palette names,
 * but each theme registers its primary under its own name and replaces gray,
 * green, red, teal and violet with its own ramps — so those had no checked
 * value. Serene Sage's primary measured 4.10:1 and its error ramp 4.39:1 in the
 * browser sweep.
 *
 * Computed here rather than listed, so adding a tenth theme needs no new
 * entries anywhere.
 */
const cssVariablesResolver: CSSVariablesResolver = (theme) => {
  const light: Record<string, string> = {};
  const dark: Record<string, string> = {};
  for (const [name, palette] of Object.entries(theme.colors)) {
    if (!palette?.[6]) {
      continue;
    }
    const onTint = lightVariantTextColors(palette);
    light[`--mantine-color-${name}-light-color`] = onTint.light;
    dark[`--mantine-color-${name}-light-color`] = onTint.dark;
    const onPage = bodyTextColors(palette);
    light[`--mantine-color-${name}-text`] = onPage.light;
    dark[`--mantine-color-${name}-text`] = onPage.dark;
  }
  return { variables: {}, light, dark };
};

export function DynamicMantineProvider({ children }: DynamicMantineProviderProps): JSX.Element {
  const { mantineThemeOverride } = useAppTheme();

  // Merge base theme with dynamic color overrides
  const theme = mergeThemeOverrides(baseTheme, mantineThemeOverride);

  return (
    <MantineProvider theme={theme} cssVariablesResolver={cssVariablesResolver}>
      {children}
    </MantineProvider>
  );
}
